import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { projectThemes } from './themes';

declare global {
  interface Window {
    __BLOG_LIST_DATA__?: {
      project: string;
      posts: any[];
    };
  }
}

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  date: string;
  excerpt: string;
  author?: string;
  category?: string;
  readingTime?: string;
}

export default function BlogHome({ project }: { project: string }) {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const theme = projectThemes[project] || projectThemes['tattvadynamics'];

  useEffect(() => {
    if (window.__BLOG_LIST_DATA__ && window.__BLOG_LIST_DATA__.project === project) {
      setPosts(window.__BLOG_LIST_DATA__.posts);
    } else {
      setPosts([]);
      loadPosts();
    }
  }, [project]);

  const loadPosts = async () => {
    try {
      let response = await fetch(`/content/blogs/${project}.json`);
      if (!response.ok && project === 'tattvadynamics') {
        response = await fetch('/content/blogs/tattvadynamics.json');
      }
      const data = await response.json();
      setPosts(data);
    } catch (e) {
      console.error("Failed to load blog posts", e);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: theme.fontFamily }}>
      <style>{`
        ::selection {
          background-color: ${theme.primary}44;
          color: white;
        }
      `}</style>
      <Helmet>
        <title>{theme.blogName}</title>
        <link rel="icon" type="image/x-icon" href={theme.favicon} />
      </Helmet>

      {/* Modern Navigation */}
      <nav className="sticky top-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img className="h-8 w-auto transition-all" src={theme.logo} alt={theme.brandText} />
            <span className="text-sm font-bold tracking-widest group-hover:opacity-100 transition-opacity">
              {theme.brandText}
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-xs font-medium uppercase tracking-widest opacity-60">
             <Link to="/" className="hover:opacity-100">Home</Link>
             <Link to="/blog" className="opacity-100" style={{ color: theme.primary }}>Blog</Link>
             <Link to="/legal" className="hover:opacity-100">Legal</Link>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-20">
        {/* Featured Section */}
        <section className="mb-24">
          <div className="flex items-center gap-2 mb-8">
            <div className="h-px w-8" style={{ backgroundColor: theme.primary }}></div>
            <span className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: theme.primary }}>Featured story</span>
          </div>
          
          {posts.length > 0 && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-12"
            >
              <div className="lg:col-span-9">
                <Link to={`/blog/${posts[0].slug}`} className="group">
                  <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight leading-[1.1] group-hover:opacity-90 transition-opacity truncate" title={posts[0].title}>
                    {posts[0].title}
                  </h1>
                  <p className="text-xl text-gray-400 leading-relaxed mb-8 line-clamp-3">
                    {posts[0].excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="font-bold uppercase tracking-widest text-[10px] opacity-50">By</span>
                    <span className="font-semibold text-white" style={{ color: theme.primary }}>{posts[0].author || theme.bloggedBy}</span>
                    <span className="opacity-20">•</span>
                    <span>{posts[0].readingTime || '5 min read'}</span>
                  </div>
                </Link>
              </div>
            </motion.div>
          )}
        </section>

        <hr className="border-white/5 mb-24" />

        {/* List Section */}
        <div className="grid grid-cols-1 gap-20">
          {posts.slice(1).map((post, i) => (
            <motion.article 
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="group"
            >
              <Link to={`/blog/${post.slug}`} className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-grow">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-white/5 border border-white/10" style={{ color: theme.primary, borderColor: `${theme.primary}33` }}>
                      {post.category || 'Engineering'}
                    </span>
                    <span className="text-xs text-gray-600">{post.date}</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-3 group-hover:opacity-70 transition-opacity truncate" title={post.title}>
                    {post.title}
                  </h2>
                  <p className="text-gray-400 leading-relaxed mb-6 line-clamp-2 max-w-3xl">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-xs tracking-wide text-gray-500">
                    <span className="font-bold uppercase tracking-widest text-[9px] opacity-40">By</span>
                    <span className="font-semibold" style={{ color: theme.primary }}>{post.author || theme.bloggedBy}</span>
                    <span className="opacity-20">•</span>
                    <span>{post.readingTime || '3 min read'}</span>
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>

        {posts.length <= 1 && posts.length > 0 && (
          <div className="text-center py-20 opacity-30 italic text-sm tracking-widest uppercase">
            More stories coming soon
          </div>
        )}
      </main>

      <footer className="py-20 border-t border-white/5 text-center px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8 opacity-40 hover:opacity-100 transition-opacity">
          <p className="text-xs uppercase tracking-[0.4em]">Essence. Engineered.</p>
          <div className="flex gap-8 text-[10px] font-bold uppercase tracking-widest">
            <Link to="/privacy">Privacy</Link>
            <Link to="/legal">Terms</Link>
            <a href="mailto:hello@tattvadynamics.com">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
